import { AddonError, Vendor } from 'mggp-player';
import MggpPluginBuffering from 'mggp-plugin-buffering';
import { DRMInitializer } from './drmInitializer';
import { getRefFromSrc } from './drmUtils';
import { disableSubtitles, resolveAudioTrack } from './videoElementUtils';
export default function MggpVendorHlsFairplay(pPlayer, pConfig) {
  Vendor.apply(this, arguments);

  const self = this;
  let _pluginBuffering = null;
  let _src = null;
  let _drmInitializer = null;
  let _videoInfo = null;
  let _video = null;
  let _startPosition = 0;
  let _started = false;
  let _paused = false;
  let _beforeCompleteFired = false;
  let _bufferingDetected = false;
  let _lastPlayPos = 0;
  let _audioToSwitch = undefined;
  let _ref = '';
  let _isInited = false;
  let _isDisposed = false;
  // '__' means that this variable is for the shitcode
  let __isInitializedLoadCalled = false;
  let __initializedOptions = undefined;

  if (self.player.get('player:usePluginBuffering')) {
    self.pluginBuffering = new MggpPluginBuffering(pPlayer, pConfig);
  }

  this.dispose = () => {
    toggleEvents(false);

    if (_drmInitializer) {
      _drmInitializer.dispose();
      _drmInitializer = null;
    }
    if (_video) {
      _video.src = '';
      _video.webkitSetMediaKeys(null);

      if (!self.player.config['singleVideoInstance']) {
        _video = null;
        self.player = null;
        self.namespase = null;
        self.config = null;
      }
    }

    _src = null;
    _videoInfo = null;
    _audioToSwitch = void 0;
    _ref = '';
    _isInited = false;
    _isDisposed = true;

    if (_pluginBuffering) {
      _pluginBuffering.dispose();
    }
  };

  this.checkOptions = (options) => {
    if ('container' in options) {
      self.container = options['container'];
    }
    if ('namespace' in options) {
      self.namespace = options['namespace'];
      if (self.player && self.namespace != self.DEFAULT_NAMESPACE) {
        self.player.registerProperty(self.namespace + 'volume', 60, true);
        self.player.registerProperty(self.namespace + 'position', 0);
        self.player.registerProperty(self.namespace + 'duration', 0);
        self.player.registerProperty(self.namespace + 'state', -1);
        self.player.registerProperty(self.namespace + 'loadedFraction', 0);
        self.player.registerProperty(self.namespace + 'muted', false, true);
      }
    }
  };

  this.load = (src, videoInfo, options) => {
    setState(5);
    if (!src) {
      src = videoInfo.src;
    }
    let ref = getRefFromSrc(src);
    if (ref !== _ref) {
      _isInited = false;
      _ref = ref;
    }
    if (!_isInited) {
      if (!_video) {
        createPlayer();
        toggleEvents(true);
      }

      __isInitializedLoadCalled = false;
      __initializedOptions = options; // need to modify 'options' in future. Yes, this is bad, but there is no way
      if (_drmInitializer) {
        _drmInitializer.dispose();
      }
      _drmInitializer = new DRMInitializer(
        src,
        _video,
        videoInfo.license_certificate,
        videoInfo.license_server,
        _ref
      );
      const prom = _drmInitializer.start();
      prom
        .then(() => {
          if (!_isDisposed) {
            _isInited = true;
            initializedLoad(src, videoInfo, __initializedOptions);
          }
        })
        .catch((errorMessage) => {
          console.error('MggpVendorHLSFairplay:', errorMessage);
          self.emit('error');
        });
    } else {
      initializedLoad(src, videoInfo, options);
    }
  };

  function initializedLoad(src, videoInfo, options) {
    if (!self.player) {
      return;
    }
    __isInitializedLoadCalled = true;

    if (options) {
      self.checkOptions(options);
    } else {
      options = {};
    }

    const initAudioTrack = resolveAudioTrack(videoInfo, options);
    self.player.set('audioTrack', initAudioTrack);
    _audioToSwitch = initAudioTrack;

    // TODO: if ('defaultQuality' in options) {
    //   let quality = getSrcOfQuality(parseInt(options['defaultQuality']));
    //   if (quality && quality.src) {
    //     src = quality.src;
    //     self.player.set('quality', quality.bitrate);
    //   }
    // }

    if ('defaultSubtitles' in options) {
      self.setSubtitles(options['defaultSubtitles']);
    }
    setState(3);

    _videoInfo = videoInfo || null;
    _src = src ? src : videoInfo.src;

    if (!_videoInfo && !_src) {
      self.emit('mediaError', { message: 'Src not found' });
      return;
    }

    _video.autoplay = true;

    if ('autoplay' in options) {
      _video.autoplay = options['autoplay'];
    }

    setVideoVolume(self.get('volume') / 100);

    if (self.get('muted')) {
      _video.muted = true;
    }

    if ('startSeconds' in options) {
      _startPosition = options.startSeconds;
      delete options.startSeconds;
    } else {
      _startPosition = void 0;
    }
    // TODO: _video.src = pSrc;
  }

  this.play = () => {
    if (_video.src) {
      _video.play();
    }
  };

  this.pause = () => {
    if (_video && !_paused) {
      _paused = true;
      _video.pause();
    }
  };

  this.stop = () => {
    if (_video) {
      if (self.player.config['singleVideoInstance'] == true) {
        _video.style.display = 'none';
      } else {
        self.container.removeChild(_video);
      }
    }

    self.dispose();
  };

  this.togglePlayPause = () => {
    if (_video) {
      self.get('state') == 1 ? self.pause() : self.play();
    }
  };

  this.seekTo = (seconds) => {
    if (!_video) {
      return;
    }
    if (typeof seconds !== 'number' || Number.isNaN(seconds)) {
      self.log('seekTo: argument "seconds" has invalid value:', seconds);
      return;
    }
    setState(5);
    let seekTime = Math.min(
      Math.max(0, seconds),
      Math.min(self.get('duration') - 10, seconds)
    );
    _video.currentTime = seekTime;
    self.set('position', seekTime);
  };

  this.setVolume = (pVolume) => {
    if (pVolume > 100) {
      pVolume = 100;
    } else if (pVolume < 0) {
      pVolume = 0;
    }
    if (_video) {
      setVideoVolume(pVolume / 100);
    }
    self.set('volume', pVolume);
  };

  this.mute = () => {
    if (self.get('muted')) {
      return;
    }
    self.set('muted', true);
    _video.muted = true;
  };

  this.unMute = () => {
    if (!self.get('muted')) {
      return;
    }
    self.set('muted', false);
    _video.muted = false;
  };

  this.getPlayableElement = () => {
    return _video;
  };

  this.setQuality = (bitrate) => {
    _audioToSwitch = self.player.get('audioTrack');
    self.emit('qualityNeedChange', bitrate);
  };

  this.setAudioTrack = (id) => {
    id = Number(id);

    if (_video) {
      let tracks = Array.from(_video.audioTracks);
      if (tracks && tracks.length > 0) {
        let reg = /\d+/;
        let audioTrackList = self.get('audioTrackList');
        let targetAudio;
        if (Array.isArray(audioTrackList)) {
          targetAudio = audioTrackList.find((track) => track.id === id);
        }
        for (let i = 0, l = tracks.length; i < l; ++i) {
          const matches = tracks[i].language.match(reg);
          let isTrackEnabled = false;
          if (matches && matches.length > 0) {
            try {
              if (
                parseInt(matches[0]) === id &&
                Boolean(audioTrackList.find((tr) => tr.id === id))
              ) {
                self.player.set('audioTrack', id);
                tracks[i].enabled = true;
                isTrackEnabled = true;
              }
            } catch (error) {
              console.error(error);
            }
          } else if (
            targetAudio &&
            (tracks[i].language === targetAudio.lang ||
              tracks[i].language === targetAudio.lang_iso_639_1)
          ) {
            self.player.set('audioTrack', id);
            tracks[i].enabled = true;
            isTrackEnabled = true;
          }
          if (!isTrackEnabled) {
            // in Safari could be enabled several tracks
            // but only 1 will play
            tracks[i].enabled = false;
          }
        }
        if (tracks.every(({ enabled }) => !enabled)) {
          tracks[0].enabled = true;
        }
      }
    } else {
      _audioToSwitch = id;
      // self.load(self.player.get('src'), self.player.get('videoInfo'), self.player.get('vendorOptions'));
    }
    disableSubtitles(_video);
  };

  function createPlayer() {
    if (self.player.config['singleVideoInstance'] == true) {
      _video = self.player.get(self.namespace + 'video');
    }

    if (!_video) {
      _video = document.createElement('video');
      self.container.appendChild(_video);
    }

    _video.className = self.namespace + 'video';
    _video.style.width = '100%';
    _video.style.height = '100%';
    _video.style.display = 'inline-block';

    if (_pluginBuffering) {
      _pluginBuffering.setVideo(_video);
    }
  }

  function setState(pNewState) {
    self.set('state', pNewState);
    self.emit('stateChange', pNewState);
  }

  function toggleEvents(pOn) {
    if (pOn) {
      if (_video) {
        _video.addEventListener('loadstart', onVideoLoadStart, false);
        _video.addEventListener('canplay', onVideoCanPlay, false);
        _video.addEventListener('pause', onVideoPause, false);
        _video.addEventListener('playing', onVideoPlaying, false);
        _video.addEventListener('seeked', onVideoSeeked, false);
        _video.addEventListener('seeking', onVideoSeeking, false);
        _video.addEventListener('volumechange', onVideoVolumeChange, false);
        _video.addEventListener('durationchange', onVideoDurationChange, false);
        _video.addEventListener('ended', onVideoEnded, false);
        _video.addEventListener('error', onVideoError, false);
      }

      if (self.player && self.player.on) {
        self.player.on('player:timeUpdate', onTimeUpdate);
      }
    } else {
      if (_video) {
        _video.removeEventListener('loadstart', onVideoLoadStart, false);
        _video.removeEventListener('canplay', onVideoCanPlay, false);
        _video.removeEventListener('pause', onVideoPause, false);
        _video.removeEventListener('playing', onVideoPlaying, false);
        _video.removeEventListener('seeked', onVideoSeeked, false);
        _video.removeEventListener('seeking', onVideoSeeking, false);
        _video.removeEventListener('volumechange', onVideoVolumeChange, false);
        _video.removeEventListener(
          'durationchange',
          onVideoDurationChange,
          false
        );
        _video.removeEventListener('ended', onVideoEnded, false);
        _video.removeEventListener('error', onVideoError, false);
      }

      if (self.player && self.player.off) {
        self.player.off('player:timeUpdate', onTimeUpdate);
      }
    }
  }

  function setVideoVolume(volume) {
    _video.removeEventListener('volumechange', onVideoVolumeChange);
    _video.volume = volume;
    setTimeout(() => {
      if (_video) {
        _video.addEventListener('volumechange', onVideoVolumeChange);
      }
    }, 0);
  }

  function onVideoError() {
    if (!_isInited) {
      return;
    }
    self.emit('mediaError', { this: self });
    setState(5);

    let errorType, errorCode, errorMessage;
    errorType = AddonError.errorTypes.NETWORK_ERROR;
    errorCode = AddonError.errorCodes[errorType].MEDIA_FILE_LOADING_ERROR;
    errorMessage = AddonError.errorMessages[errorCode] + _src;
    new AddonError(self.player, errorType, errorCode, errorMessage, true);
  }

  function onVideoLoadStart() {
    setState(3);
  }

  function onVideoCanPlay() {
    if (!__isInitializedLoadCalled) {
      // there is some strange behavior with drmInitializer
      // if you are on dvr channel and do the 'long' seek (this vendor re-instantiates many times)
      // the drmInitializer's promise didn't resolved (because something went wrong with safari's drm initialization)
      // but in fact the drm has initialized
      // so in this case call the 'initializedLoad' when videoElement is ready
      initializedLoad(
        self.player.get('src'),
        self.player.get('videoInfo'),
        __initializedOptions || self.player.get('options')
      );
    }

    if (!_started) {
      self.emit('mediaCanPlay');
      if (_video && _video.autoplay) {
        _video.play();
      }
    }
    if (_video.paused && self.get('state') === 3) {
      setState(2);
    }
    if (_audioToSwitch !== undefined) {
      self.setAudioTrack(_audioToSwitch);
      _audioToSwitch = undefined;
    }
    if (_startPosition !== undefined) {
      self.seekTo(_startPosition);
      _startPosition = undefined;
    }
    disableSubtitles(_video);
  }

  function onVideoPause() {
    if (_paused) {
      setState(2);
    } else if (_video.paused) {
      _lastPlayPos = _video.currentTime;
      setState(5);
    }
  }

  function onVideoPlaying() {
    if (!__isInitializedLoadCalled) {
      // there is some strange behavior with drmInitializer
      // if you are on dvr channel and do the 'long' seek (this vendor re-instantiates many times)
      // the drmInitializer's promise didn't resolved (because something went wrong with safari's drm initialization)
      // but in fact the drm has initialized
      // so in this case call the 'initializedLoad' when videoElement is ready
      initializedLoad(
        self.player.get('src'),
        self.player.get('videoInfo'),
        __initializedOptions || self.player.get('options')
      );
    }
    _started = true;
    _paused = false;
    setState(1);
  }

  function onVideoSeeked() {
    self.emit('mediaSeekEnd');
    setState(_video.paused ? 2 : 1);
  }

  function onVideoSeeking() {
    self.emit('mediaSeekStart');
    setState(4);
  }

  function onVideoVolumeChange(event) {
    if (self.player.get('muted') !== event.currentTarget.muted) {
      self[event.currentTarget.muted ? 'mute' : 'unMute']();
    } else {
      self.set('volume', _video.volume * 100);
    }
  }

  function onVideoDurationChange() {
    self.set('duration', _video.duration);
  }

  function onTimeUpdate() {
    let position = self.get('position');
    let duration = self.get('duration');
    if (_video.currentTime != position) {
      self.set('position', _video.currentTime);
    }

    if (duration > 0 && duration - position < 1 && !_beforeCompleteFired) {
      _beforeCompleteFired = true;
      self.emit('mediaBeforeComplete');
    }

    for (let i = 0; i < _video.buffered.length; i++) {
      if (
        _video.buffered.start(i) <= position &&
        _video.buffered.end(i) > position
      ) {
        self.set('loadedFraction', _video.buffered.end(i) / duration);
        break;
      }
    }

    let offset =
      1 /
      ('timeInterval' in self.config ? self.config['timeInterval'] : 1000 / 3);

    let currentTime = parseInt(_video.currentTime);
    let lastPositionWithOffset = parseInt(_lastPlayPos + offset);

    if (
      !_bufferingDetected &&
      currentTime < lastPositionWithOffset &&
      !_paused &&
      _started
    ) {
      _bufferingDetected = true;
      setState(3);
    }

    if (self.get('state') === 4 && !_video.paused && !_video.seeking) {
      setState(1);
    }

    if (
      _bufferingDetected &&
      currentTime > lastPositionWithOffset &&
      !_paused
    ) {
      _bufferingDetected = false;
      setState(1);
    }

    if (
      Math.abs(_video.currentTime - _lastPlayPos) > 2 &&
      !_paused &&
      self.player.get('state') !== 1
    ) {
      setState(1);
    }

    _lastPlayPos = _video.currentTime;
  }

  function onVideoEnded() {
    setState(0);
    toggleEvents(false);
    _started = false;
  }

  self.log('MggpVendorHlsFairplay:Created!');
}

MggpVendorHlsFairplay.prototype = Object.create(Vendor.prototype);
MggpVendorHlsFairplay.prototype.constructor = MggpVendorHlsFairplay;

MggpVendorHlsFairplay.canPlay = function (format, config, options) {
  const isDrm = options && 'isDrm' in options ? options.isDrm : config.drm;
  return MggpVendorHlsFairplay.mimeTypes.indexOf(format) !== -1 && isDrm;
};

MggpVendorHlsFairplay.priority = 0;
MggpVendorHlsFairplay.mimeTypes = ['m3u8'];
